import { pipelineToolDefinition } from "./pipeline-tool-registry.js";

/** Semantic sinks the planner must be able to name after parse. */
export const PIPELINE_REQUIRED_ROLES = [
  "auction_window",
  "close",
  "ready",
  "hold",
  "blocked"
] as const;
export type PipelineRequiredRole = (typeof PIPELINE_REQUIRED_ROLES)[number];

export type PipelineRoleStep = {
  id: string;
  kind: string;
  tools: readonly string[];
  miss?: string | undefined;
  gate?: {
    auctionWindowDays?: number | undefined;
    dateRefreshCadence?: "weekly" | "never" | undefined;
  } | undefined;
};

const ROLE_KIND: Record<PipelineRequiredRole, string> = {
  auction_window: "gate",
  close: "destination",
  ready: "terminal",
  hold: "terminal",
  blocked: "terminal"
};

export function pipelineStepHasFallbackGroup(
  step: { tools: readonly string[] },
  group: string
): boolean {
  return step.tools.some((id) => pipelineToolDefinition(id)?.fallbackGroup === group);
}

export function pipelineStepRole(step: PipelineRoleStep): PipelineRequiredRole | null {
  if (step.id === "hold" || step.id === "ready" || step.id === "blocked") return step.id;
  if (step.tools.includes("close_create")) return "close";
  if (step.kind === "gate" && step.gate?.auctionWindowDays != null) return "auction_window";
  return null;
}

export function pipelineStepForRole<T extends PipelineRoleStep>(
  document: { steps: readonly T[] },
  role: PipelineRequiredRole
): T | null {
  return document.steps.find((step) => pipelineStepRole(step) === role) ?? null;
}

export function pipelineRequiredRoleMissingMessage(role: PipelineRequiredRole): string {
  if (role === "hold") {
    return 'Required terminal path missing: step "hold" cannot be removed. Cancelled, completed, and waiting sales use this sink.';
  }
  if (role === "ready") {
    return 'Required terminal path missing: step "ready" cannot be removed. Ready-to-call delivery uses this sink.';
  }
  if (role === "blocked") {
    return 'Required terminal path missing: step "blocked" cannot be removed. Failed enrichments need this sink.';
  }
  if (role === "close") {
    return "Required destination missing: a close_create step is required.";
  }
  return "Required auction-window gate missing. Keep a gate step with auctionWindowDays so window policy stays explicit.";
}

/** Actionable issues for missing, duplicated, or misplaced required roles. Empty = valid. */
export function pipelineWorkflowRoleIssues(document: {
  steps: ReadonlyArray<PipelineRoleStep>;
}): string[] {
  const issues: string[] = [];
  const found = new Map<PipelineRequiredRole, string>();
  for (const step of document.steps) {
    const role = pipelineStepRole(step);
    if (!role) continue;
    const prior = found.get(role);
    if (prior) {
      issues.push(
        `Required path "${role}" is duplicated on steps "${prior}" and "${step.id}". Keep one.`
      );
    } else {
      found.set(role, step.id);
    }
    const expectedKind = ROLE_KIND[role];
    if (step.kind !== expectedKind) {
      issues.push(
        `Required path "${role}" on step "${step.id}" must remain kind ${expectedKind}.`
      );
    }
    if ((role === "hold" || role === "ready") && step.miss !== "hold") {
      issues.push(`Required terminal path "${role}" must keep miss=hold.`);
    }
    if (role === "blocked" && step.miss !== "block") {
      issues.push(`Required terminal path "blocked" must keep miss=block.`);
    }
  }
  for (const role of PIPELINE_REQUIRED_ROLES) {
    if (!found.has(role)) issues.push(pipelineRequiredRoleMissingMessage(role));
  }
  const gateIndex = document.steps.findIndex((step) => pipelineStepRole(step) === "auction_window");
  const workIndex = document.steps.findIndex(
    (step) => step.kind === "enrichment" || step.kind === "destination"
  );
  if (gateIndex >= 0 && workIndex >= 0 && gateIndex > workIndex) {
    const workId = document.steps[workIndex]?.id ?? "enrichment";
    issues.push(
      `Required auction-window gate must stay before enrichment. Move it earlier so window policy applies before "${workId}".`
    );
  }
  return [...new Set(issues)];
}
